import React from 'react';
import { ArrowDefs, Node, Pill } from './dg';

/* ------------------------------------------------------------------ */
/* 4. The ask flow: question → routing → validator → answer           */
/* ------------------------------------------------------------------ */

export function DiagramAsk() {
    return (
        <svg
            viewBox="0 0 720 196"
            role="img"
            aria-label="A question is routed to one of nine read-only tools, a validator checks every number, and the answer renders with its path."
        >
            <ArrowDefs id="d4a" />
            <Node x={24} y={40} w={140} h={78} title="question" sub="plain English" />
            <Node
                x={188}
                y={40}
                w={180}
                h={78}
                title="routing"
                sub="picks 1 of 9 tools"
                sub2="LLM optional"
            />
            <Node x={392} y={40} w={130} h={78} title="validator" sub="checks numbers" />
            <Node x={546} y={40} w={120} h={78} title="answer" sub="path shown" />
            <line x1={164} y1={79} x2={186} y2={79} className="d-edge" markerEnd="url(#d4a)" />
            <line x1={368} y1={79} x2={390} y2={79} className="d-edge" markerEnd="url(#d4a)" />
            <line x1={522} y1={79} x2={544} y2={79} className="d-edge" markerEnd="url(#d4a)" />
            <text x="360" y="158" textAnchor="middle" className="d-sub">
                nine reads: who_knows · critical_people · knowledge_islands · module_risk ·
                simulate_departure
            </text>
            <text x="360" y="178" textAnchor="middle" className="d-sub">
                handover_plan · heal_and_verify · recall · relations
            </text>
        </svg>
    );
}

/* ------------------------------------------------------------------ */
/* 5. Three worlds: truth, drill, heal                                */
/* ------------------------------------------------------------------ */

const miniNodes = (ox, oy) => [
    [ox + 44, oy + 80],
    [ox + 80, oy + 66],
    [ox + 116, oy + 80],
    [ox + 60, oy + 104],
    [ox + 96, oy + 104],
];

function MiniEdges({ pts, cls = 'd-edge-thin' }) {
    const [a, b, c, d, e] = pts;
    const pairs = [
        [a, b],
        [b, c],
        [a, d],
        [d, e],
        [e, c],
    ];
    return pairs.map(([p, q], i) => (
        <line key={i} x1={p[0]} y1={p[1]} x2={q[0]} y2={q[1]} className={cls} />
    ));
}

function MiniDots({ pts, dashedIndex = -1, tones = {} }) {
    return pts.map(([x, y], i) => {
        const tone = i === dashedIndex ? 'd-danger' : tones[i] || '';
        return (
            <g key={i} className={tone}>
                <circle cx={x} cy={y} r="6.5" className="d-mini-dot" />
            </g>
        );
    });
}

export function DiagramWorlds() {
    const truth = miniNodes(266, -18);
    const drill = miniNodes(16, 180);
    const heal = miniNodes(374, 180);
    return (
        <svg
            viewBox="0 0 720 330"
            role="img"
            aria-label="Truth graph is copied with GRAPH.COPY into a drill world where one person is removed and a module strands, and into a heal world where the module is reconnected."
        >
            <ArrowDefs id="d5a" />

            <rect x="255" y="10" width="210" height="98" rx="14" className="d-panel" />
            <text x="360" y="34" textAnchor="middle" className="d-panel-title">
                truth — the repo
            </text>
            <MiniEdges pts={truth} />
            <MiniDots pts={truth} />

            <rect x="16" y="180" width="330" height="130" rx="14" className="d-panel" />
            <text x="181" y="206" textAnchor="middle" className="d-panel-title">
                drill — what-if world
            </text>
            <text x="181" y="224" textAnchor="middle" className="d-sub">
                remove one person · recompute
            </text>
            <MiniEdges pts={drill} />
            <MiniDots pts={drill} dashedIndex={2} />
            <line
                x1="248"
                y1="261"
                x2="150"
                y2="261"
                className="d-edge-danger"
                strokeDasharray="5 5"
            />
            <g className="d-danger">
                <rect x="270" y="246" width="44" height="30" rx="7" className="d-box" />
            </g>
            <text x="292" y="292" textAnchor="middle" className="d-note-danger">
                stranded
            </text>

            <rect x="374" y="180" width="330" height="130" rx="14" className="d-panel" />
            <text x="539" y="206" textAnchor="middle" className="d-panel-title">
                heal — verified world
            </text>
            <text x="539" y="224" textAnchor="middle" className="d-sub">
                apply the plan · re-run the drill
            </text>
            <MiniEdges pts={heal} />
            <MiniDots pts={heal} />
            <g className="d-ok">
                <rect x="630" y="246" width="44" height="30" rx="7" className="d-box" />
            </g>
            <line x1="628" y1="261" x2="504" y2="261" className="d-edge-ok" />
            <text x="652" y="292" textAnchor="middle" className="d-note-ok">
                reconnected
            </text>

            <line
                x1="300"
                y1="108"
                x2="122"
                y2="178"
                className="d-edge"
                markerEnd="url(#d5a)"
            />
            <Pill x={210} y={144} text="GRAPH.COPY" />
            <line
                x1="420"
                y1="108"
                x2="598"
                y2="178"
                className="d-edge"
                markerEnd="url(#d5a)"
            />
            <Pill x={515} y={144} text="GRAPH.COPY" />
        </svg>
    );
}

/* ------------------------------------------------------------------ */
/* 6. The bridge: shortest weighted path through shared work          */
/* ------------------------------------------------------------------ */

export function DiagramBridge() {
    return (
        <svg
            viewBox="0 0 720 140"
            role="img"
            aria-label="Path: Rene Groeschke knows rest-api-spec, which Benjamin Trent also knows, and Benjamin knows client/rest-high-level."
        >
            <ArrowDefs id="d6a" />
            <Node x={24} y={42} w={132} h={66} title="Rene Groeschke" sub="the successor" />
            <Node x={208} y={42} w={132} h={66} title="rest-api-spec" sub="shared work" />
            <Node x={392} y={42} w={132} h={66} title="Benjamin Trent" sub="the sole knower" />
            <Node x={576} y={42} w={132} h={66} title="client/" sub="rest-high-level" tone="accent" />
            <line x1={162} y1={75} x2={202} y2={75} className="d-edge" markerEnd="url(#d6a)" />
            <line x1={346} y1={75} x2={386} y2={75} className="d-edge" markerEnd="url(#d6a)" />
            <line x1={530} y1={75} x2={570} y2={75} className="d-edge" markerEnd="url(#d6a)" />
            <text x="182" y="96" textAnchor="middle" className="d-sub">
                KNOWS
            </text>
            <text x="366" y="96" textAnchor="middle" className="d-sub">
                KNOWS
            </text>
            <text x="550" y="96" textAnchor="middle" className="d-sub">
                KNOWS
            </text>
        </svg>
    );
}
